import React from 'react';
import { Star, Cloud, Eye, Pencil } from 'lucide-react';
import type { LibraryWorkspace } from '../../utils/workspaceLibrary';
import type { FolderColor } from '../../utils/workspaceFolders';
import { formatCost, formatRelativeTime } from '../../utils/workspaceLibrary';
import { LibraryMenu, type MenuEntry } from './LibraryMenu';
import { colorTone } from './colors';
import { WorkspaceThumb } from './ProjectTile';

export interface WorkspaceCardProps {
    workspace: LibraryWorkspace;
    /** e.g. "Art direction / Characters" in Recent, Starred and search results. */
    locationLabel?: string;
    /** The colour of the project it is in: the dot before its name */
    color?: FolderColor;
    onOpen: () => void;
    /** Omitted for shared workspaces (the recipient cannot star them). */
    onToggleFavorite?: () => void;
    menuItems: MenuEntry[];
    draggable?: boolean;
    onDragStart?: (e: React.DragEvent) => void;
    onDragEnd?: () => void;
    dragging?: boolean;
}

/**
 * A workspace as a picture tile: its newest picture, then its name with the dot of its
 * project's colour and when it was last touched. What is shared, local or unsynced is said on
 * the picture; the star and the actions show on hover.
 */
export const WorkspaceCard: React.FC<WorkspaceCardProps> = ({
    workspace,
    locationLabel,
    color,
    onOpen,
    onToggleFavorite,
    menuItems,
    draggable,
    onDragStart,
    onDragEnd,
    dragging,
}) => {
    const { name, thumbnail, isLocal, isShared, sharedPermission, syncedToCloud, isFavorite, lastSaved, totalCost } = workspace;

    return (
        <div
            role="listitem"
            className="ns-lib-card ns-lib-card-tile"
            data-dragging={dragging ? 'true' : undefined}
            draggable={draggable}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            data-testid={`workspace-card-${workspace.id}`}
        >
            <button
                type="button"
                className="text-left w-full min-w-0 outline-none focus-visible:ring-4 focus-visible:ring-lavender-soft rounded-2xl p-1.5"
                onClick={onOpen}
                aria-label={`Open ${name}`}
            >
                <div className="relative aspect-[16/10] rounded-xl overflow-hidden bg-cream-200 text-ink-faint grid place-items-center">
                    {thumbnail ? (
                        <img src={thumbnail} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" draggable={false} />
                    ) : (
                        <WorkspaceThumb px={44} className="rounded-none bg-transparent" />
                    )}
                    <div className="absolute left-2 top-2 flex flex-wrap gap-1 max-w-[calc(100%-16px)]">
                        {isLocal && <span className="ns-chip ns-chip-sm ns-chip-mint">On this device</span>}
                        {isShared && (
                            <span className="ns-chip ns-chip-sm ns-glass ns-glass-strong text-ink">
                                {sharedPermission === 'edit' ? <Pencil className="w-3 h-3" aria-hidden="true" /> : <Eye className="w-3 h-3" aria-hidden="true" />}
                                {sharedPermission === 'edit' ? 'edit' : 'view'}
                            </span>
                        )}
                        {!isLocal && !isShared && !syncedToCloud && (
                            <span className="ns-chip ns-chip-sm ns-chip-warn"><Cloud className="w-3 h-3" aria-hidden="true" />Not synced</span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-1.5 pl-1.5 pr-9 pt-2 pb-1 min-w-0">
                    {!isShared && <span className="ns-tone-dot w-[7px] h-[7px]" data-tone={colorTone(color)} aria-hidden="true" />}
                    <h3 className="text-[13px] font-semibold text-ink truncate flex-1 min-w-0" title={name}>{name}</h3>
                    <span className="ns-t-meta whitespace-nowrap flex-none" title={locationLabel ? undefined : `Edited ${formatRelativeTime(lastSaved)}`}>
                        {locationLabel ? locationLabel : formatRelativeTime(lastSaved)}
                    </span>
                </div>
                {totalCost && totalCost > 0 ? (
                    <p className="ns-t-meta pl-1.5 pb-1 tabular-nums" data-card-cost>{formatCost(totalCost)}</p>
                ) : null}
            </button>

            {onToggleFavorite && (
                <button
                    type="button"
                    className="ns-icon-btn ns-icon-btn-dark ns-lib-card-reveal absolute top-3 right-3"
                    data-always={isFavorite ? 'true' : undefined}
                    aria-pressed={isFavorite}
                    aria-label={isFavorite ? `Unstar ${name}` : `Star ${name}`}
                    title={isFavorite ? 'Unstar' : 'Star'}
                    onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
                    draggable={false}
                >
                    <Star className={isFavorite ? 'fill-lemon text-lemon' : ''} aria-hidden="true" />
                </button>
            )}

            <div className="absolute bottom-1 right-1">
                <LibraryMenu label={`More actions for ${name}`} items={menuItems} triggerClassName="ns-icon-btn ns-icon-btn-sm ns-lib-card-reveal ns-lib-card-reveal-soft" direction="up" />
            </div>
        </div>
    );
};
